// STEP 5, `/verdict` — THE APP'S "VALUE" (WEB-PARITY-4: VD-10 to VD-18, AN-2).
//
// The app's screen (`ValueView`, AppealAce/Features/Reasons/ValueView.swift) laid out for a wide screen: "Your requested
// value" and its hero sentence; "Assessed value looks too low"; the "Requested value" card — the purchase warning, "You're
// requesting $X off", the row that opens the suggested-value sheet, "Why we stopped here", "Capped at what you paid" and
// the submarket note — or the field the owner types their own figure into; "Set aside as outliers" with "Keep this comp
// anyway"; "Adjust for condition (optional)"; "Check uniformity (optional)" where the state has a track; the case-strength
// card; "Grounds for your appeal" with the record-error note; and "Continue to generate", which routes through the
// "Quick check" where the ask is a quarter or more below the assessment.
//
// WHAT THE SERVER DECIDES AND THIS PAGE ONLY PRINTS: the value, the ask and every guard on it (`v2Value`), the meter, the
// readout, the equity verdict and the grounds the step ticks on arrival (`v2EvaluateCase`), whether Continue is open
// (NEW-21) and whether the Quick check is owed (NEW-15). The owner's choices go back through the server's own doors:
// `setAsk` for the figure, `evidence` for the grounds, the note, the equity ground and the Quick check, `edit` to keep an
// outlier. Every word is the app's (`parity4Wording.ts`) or the server's.

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type {
  AppealSnapshot, AskSource, EvaluateCaseResponse, V2Failure, V2Result, ValueEvidenceInput, ValueResponse,
} from "@contract";
import { useAppealId, useSession } from "../../app/session";
import { money } from "../../format";
import { PaidRoute } from "../../components/PaidRoute";
import { Refusal } from "../../components/Refusal";
import { Card4, Icon, MeterBars, ProgressLines4, Said4 } from "./parity4Parts";
import {
  assessedNote, capitalized, cappedBody, estimatePhrase, heroSubtitle, meterHeadline, methodLabel, percentString,
  precisionLabel, purchaseWarning, ratioPct, ratioTerm, SELECTABLE_REASONS, stoppedHere, strongestLine, toggled,
  uniformityState, type SelectableReason,
} from "./parity4Rules";
import { say4, say4With, type Parity4WordingId } from "./parity4Wording";
import { QuickCheck, SuggestedValueSheet } from "./valueSheets";

const REASON_ICON: Record<SelectableReason, Parameters<typeof Icon>[0]["name"]> = {
  overvaluation: "trend", comparableSales: "house", conditionIssues: "wrench", factualError: "doc",
};

export function Verdict() {
  const session = useSession();
  const appealId = useAppealId();
  return (
    <PaidRoute<ValueResponse>
      probe={() => session.transport.v2Value.compute({ appealId })}
      deps={[session.transport, appealId, session.revision]}
    >
      {(value, appeal) => <ValueStep first={value} firstAppeal={appeal} />}
    </PaidRoute>
  );
}

function ValueStep({ first, firstAppeal }: { first: ValueResponse; firstAppeal: AppealSnapshot }) {
  const session = useSession();
  const appealId = useAppealId();
  const navigate = useNavigate();
  const [value, setValue] = useState(first);
  const [check, setCheck] = useState<EvaluateCaseResponse | null>(null);
  const [failure, setFailure] = useState<V2Failure | null>(null);
  const [busy, setBusy] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [quick, setQuick] = useState(false);
  const seeded = useRef(false);
  const appeal = value.appeal ?? firstAppeal;
  const subject = appeal.subject;
  const state = subject?.state ?? null;
  const v = value.body.value;
  const reasons = value.body.grounds.reasons;

  /** The readouts that read the grounds and the ask: asked again after every change, never on a stale state. */
  const recheck = useCallback(async () => {
    const r = await session.transport.v2EvaluateCase({ appealId, narrative: false });
    // A refusal is the server's sentence on the page (R112), never a meter that silently stops answering.
    if (r.ok) setCheck(r.response);
    else setFailure(r);
    return r.ok ? r.response : null;
  }, [session.transport, appealId]);

  async function call(run: () => Promise<V2Result<ValueResponse>>): Promise<ValueResponse | null> {
    setBusy(true);
    const r = await run();
    setBusy(false);
    if (!r.ok) {
      setFailure(r);
      return null;
    }
    setFailure(null);
    setValue(r.response);
    void recheck();
    return r.response;
  }

  const evidence = (input: Omit<ValueEvidenceInput, "appealId">) =>
    call(() => session.transport.v2Value.evidence({ appealId, ...input }));
  const setAsk = (requestedValue: number | null, source: AskSource) =>
    call(() => session.transport.v2Value.setAsk({
      appealId, requestedValue: requestedValue === null ? null : { basis: "market", amount: Math.round(requestedValue) }, source,
    }));

  // THE GROUNDS ARRIVE PRE-CHECKED, ONCE (`seedReasonsIfNeeded`, :1760-1806): where none are on file the step writes the
  // server's seed, as the app writes its own on appear. A tick the owner has made is never overwritten.
  useEffect(() => {
    void (async () => {
      const c = await recheck();
      if (c === null || seeded.current) return;
      seeded.current = true;
      if (first.body.grounds.reasons.length === 0 && c.body.groundsSeed.length > 0) {
        await evidence({ reasons: [...c.body.groundsSeed] });
      }
    })();
    // The seed is the first arrival's only.
  }, []);

  const suggestion = v.suggestion;
  const new21 = value.decisions["NEW-21"];
  const canContinue = new21.ran && new21.value.hasGround;
  const new15 = value.decisions["NEW-15"];
  const owesQuickCheck = new15.ran && new15.value.isLargeGap && !new15.value.confirmed;

  function proceed() {
    if (owesQuickCheck && v.largeGap !== null) {
      setQuick(true);
      return;
    }
    navigate("/you");
  }

  return (
    <div className="value-step" data-step="value">
      <header className="step-hero">
        <span className="hero-disc" aria-hidden="true"><Icon name="target" size={24} /></span>
        <Said4 id="value.hero.title" as="h1" />
        <p className="sub" data-hero-case={v.hero.kind}>{heroSubtitle(v.hero)}</p>
      </header>

      {failure !== null && <Refusal failure={failure} context="This step" />}

      <div className="value-grid">
        <div className="value-col">
          {v.assessedLooksTooLow && subject?.assessedValue != null && (
            <Card4 icon="warn" titleId="value.tooLow.title" tint="accent" data-too-low>
              <Said4 id="value.tooLow.body" as="p" className="muted small" values={{ assessed: money(subject.assessedValue.value) }} />
              <button type="button" className="link-button" onClick={() => navigate("/details")} data-held-copy="value.tooLow.fix">
                <Icon name="back" size={14} /> {say4("value.tooLow.fix")}
              </button>
            </Card4>
          )}

          <RequestedValue value={value} state={state} onOpen={() => setSheet(true)} onTyped={(n) => void setAsk(n, "owner")} busy={busy} />

          {v.excludedOutliers.length > 0 && (
            <Card4 icon="warn" titleId="value.outliers.title" tint="accent" footer={<Said4 id="value.outliers.footer" />} data-outliers>
              {v.excludedOutliers.map((o) => (
                <div key={o.compId} className="outlier" data-outlier={o.compId}>
                  <b>{o.address.trim() === "" ? say4("value.outliers.unnamed") : o.address}</b>
                  <p className="muted small" data-copy-code={o.reason.code}>{o.reason.text}</p>
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    data-held-copy="value.outliers.keep"
                    onClick={async () => {
                      setBusy(true);
                      const r = await session.transport.v2SearchComps.edit({ appealId, keepDespiteOutlier: [o.compId] });
                      setBusy(false);
                      if (!r.ok) return setFailure(r);
                      await call(() => session.transport.v2Value.compute({ appealId }));
                    }}
                  >
                    <Icon name="back" size={14} /> {say4("value.outliers.keep")}
                  </button>
                </div>
              ))}
            </Card4>
          )}

          <ConditionCard value={value} state={state} busy={busy} onRun={() => void evidence({})} onUse={(n) => void setAsk(n, "engine")} />

          {check?.body.uniformity != null && (
            <UniformityCard
              check={check}
              value={value}
              state={state}
              busy={busy}
              onAdopt={async (request) => {
                const r = await evidence({ reasons: reasons.includes("uniformity") ? [...reasons] : [...reasons, "uniformity"], uniformity: { adopt: true } });
                if (r !== null && request !== null) await setAsk(request, "equity");
              }}
              onWithdraw={async () => {
                const r = await evidence({ reasons: reasons.filter((x) => x !== "uniformity"), uniformity: { adopt: false } });
                // The app restores the ask it held before adopting; v2 keeps no such snapshot, so an equity-sourced ask
                // is cleared and the engine's suggestion stands again (F-WP4-5).
                if (r !== null && r.body.ask.source === "equity") await setAsk(null, "engine");
              }}
            />
          )}
        </div>

        <div className="value-col">
          {v.hero.kind !== "acquisitionNoCase" && check !== null && <StrengthCard check={check} value={value} />}

          <Card4 icon="checkCircle" titleId="grounds.title" tint="primary" footer={<Said4 id="grounds.footer" />} data-grounds>
            <div className="reason-tiles">
              {SELECTABLE_REASONS.map((reason) => (
                <ReasonTile
                  key={reason}
                  reason={reason}
                  selected={reasons.includes(reason)}
                  disabled={busy}
                  onToggle={() => void evidence({ reasons: toggled(reasons, reason) as ValueEvidenceInput["reasons"] })}
                >
                  {reason === "conditionIssues" && reasons.includes("conditionIssues")
                    && value.body.condition.note.trim() === "" && value.body.photos.length === 0 && (
                    <div className="reason-nudge" data-nudge="condition">
                      <Said4 id="grounds.nudge.title" as="p" className="nudge-head" />
                      <Said4 id="grounds.nudge.body" as="p" className="muted small" />
                      <button type="button" className="btn primary small" onClick={() => navigate("/condition")} data-held-copy="grounds.nudge.back">
                        <Icon name="back" size={14} /> {say4("grounds.nudge.back")}
                      </button>
                    </div>
                  )}
                  {reason === "factualError" && reasons.includes("factualError") && (
                    <RecordNote saved={value.body.grounds.recordErrorNote} onSave={(text) => void evidence({ recordErrorNote: text })} />
                  )}
                </ReasonTile>
              ))}
            </div>
          </Card4>
        </div>
      </div>

      <div className="step-footer" data-continue={canContinue ? "open" : "closed"}>
        {!canContinue && <Said4 id="value.pickGround" as="p" className="requirement" />}
        <button type="button" className="btn primary" disabled={!canContinue || busy} onClick={proceed} data-held-copy="value.continue">
          {say4("value.continue")}
        </button>
      </div>

      {sheet && suggestion !== null && (
        <SuggestedValueSheet
          value={value}
          uniformity={check?.body.uniformity ?? null}
          state={state}
          assessed={subject?.assessedValue?.value.amount ?? null}
          onCancel={() => setSheet(false)}
          onUse={async (amount, source) => {
            setSheet(false);
            await setAsk(amount, source);
          }}
        />
      )}
      {quick && v.largeGap !== null && (
        <QuickCheck
          gap={v.largeGap}
          onCancel={() => setQuick(false)}
          onAcknowledge={async (confirmed) => {
            const r = await evidence({ largeGap: { confirmed: true, reasons: confirmed } });
            setQuick(false);
            if (r !== null) navigate("/you");
          }}
          onFixRecord={async () => {
            const r = await evidence({ reasons: reasons.includes("factualError") ? [...reasons] : [...reasons, "factualError"] as ValueEvidenceInput["reasons"] });
            setQuick(false);
            if (r !== null) navigate("/details");
          }}
        />
      )}
    </div>
  );
}

// ── "Requested value" (ValueView.swift:354-632) ────────────────────────────────────────────────────────────────────

function RequestedValue({
  value, state, onOpen, onTyped, busy,
}: {
  value: ValueResponse;
  state: string | null;
  onOpen: () => void;
  onTyped: (n: number | null) => void;
  busy: boolean;
}) {
  const v = value.body.value;
  const s = v.suggestion;
  const ask = value.body.ask;
  const g6 = value.decisions["G-6"];
  const requestedAssessed = g6.ran ? g6.value.requestedAssessed : null;
  const subject = value.appeal?.subject ?? null;

  if (s !== null) {
    const new8 = value.decisions["NEW-8"];
    const warn = new8.ran ? new8.value.warn : null;
    const new10 = value.decisions["NEW-10"];
    const capped = new10.ran && new10.value !== null && new10.value.by === "purchase" ? new10.value : null;
    const shown = ask.requestedValue ?? s.value;
    const note = assessedNote(ask.requestedValue === null ? s.valueAssessed : requestedAssessed, v.ratio, state);
    const rowLabel: Parity4WordingId = ask.requestedValue === null
      ? (v.ratio.fractional ? "value.row.suggestedMarket" : "value.row.suggested")
      : (v.ratio.fractional ? "value.row.requestingMarket" : "value.row.requesting");
    return (
      <Card4 icon="target" titleId="value.card.title" tint="primary" footer={<Said4 id="value.card.footer" />} data-requested>
        {warn !== null && subject?.lastSale != null && (
          <p className="purchase-warning" role="note">
            <Icon name="warn" size={14} />
            <span>{purchaseWarning(subject.lastSale.price, warn.purchaseWhen, warn.percentBelow)}</span>
          </p>
        )}
        {ask.estimatedReduction !== null && (
          <div className="requesting-hero" data-requesting>
            <span className="requesting-disc" aria-hidden="true"><Icon name="arrowDown" size={26} /></span>
            <div>
              <Said4 id="value.requesting.eyebrow" as="p" className="eyebrow muted-eyebrow" />
              <p className="requesting-amount" data-held-copy="value.requesting.off">
                {say4With("value.requesting.off", { amount: money(ask.estimatedReduction.amount) })}
              </p>
              <Said4 id="value.requesting.basis" as="p" className="faint" values={{ percent: ask.estimatedReduction.percent }} />
            </div>
          </div>
        )}
        <button type="button" className="value-row" onClick={onOpen} disabled={busy} data-open-sheet>
          <span className="value-row-text">
            <Said4 id={rowLabel} as="small" />
            <b>{money(shown)}</b>
            {note !== null && <small className="faint">{note}</small>}
          </span>
          <Said4 id={ask.requestedValue === null ? "value.row.review" : "value.row.change"} as="span" className="value-row-action" />
          <Icon name="chevron" size={16} />
        </button>
        {v.contradicting !== null && ask.requestedValue === null && (() => {
          const c = stoppedHere(v.contradicting);
          return (
            <div className="value-aside" data-stopped-here>
              <Said4 id="value.stopped.title" as="p" className="aside-head" />
              <p className="muted small">{c.body}</p>
              <p className="faint">{c.lead}</p>
            </div>
          );
        })()}
        {capped !== null && subject?.lastSale != null && (
          <div className="value-aside" data-capped>
            <Said4 id="value.capped.title" as="p" className="aside-head" />
            <p className="muted small">{cappedBody(capped.clampedTo, subject.lastSale.date)}</p>
            <Said4 id="value.capped.lead" as="p" className="faint" />
          </div>
        )}
        {s.submarketHeterogeneous && (
          <p className="faint submarket"><Icon name="range" size={14} /> <Said4 id="value.submarket" /></p>
        )}
      </Card4>
    );
  }

  if (v.hero.kind === "acquisitionNoCase") {
    return (
      <Card4 icon="shield" titleId="value.noCase.title" tint="muted" footer={<Said4 id="value.noCase.footer" />} data-no-number>
        <Said4 id="value.noCase.body" as="p" className="muted" />
      </Card4>
    );
  }

  return (
    <ManualAsk
      value={value}
      state={state}
      fractional={v.ratio.fractional}
      requestedAssessed={requestedAssessed}
      onTyped={onTyped}
      busy={busy}
    />
  );
}

/** The field the owner types their own figure into, where the comps produced none (ValueView.swift:534-632). The
 *  figure is MARKET in a fractional county and the label says so; the conversion line is the server's answer to it. */
function ManualAsk({
  value, state, fractional, requestedAssessed, onTyped, busy,
}: {
  value: ValueResponse;
  state: string | null;
  fractional: boolean;
  requestedAssessed: { amount: number } | null;
  onTyped: (n: number | null) => void;
  busy: boolean;
}) {
  const ask = value.body.ask.requestedValue;
  const [text, setText] = useState(ask === null ? "" : String(ask.amount));
  useEffect(() => setText(ask === null ? "" : String(ask.amount)), [ask]);
  const commit = () => {
    const digits = text.replace(/[^0-9.]/g, "");
    const n = digits === "" ? null : Number(digits);
    if (n !== null && !Number.isFinite(n)) return;
    if ((n ?? null) === (ask?.amount ?? null)) return;
    onTyped(n);
  };
  const label: Parity4WordingId = fractional ? "value.manual.labelMarket" : "value.manual.label";
  return (
    <Card4
      icon="target"
      titleId="value.card.title"
      tint="primary"
      footer={<Said4 id={fractional ? "value.manual.footerMarket" : "value.manual.footer"} />}
      data-manual-ask
    >
      <label className="manual-field">
        <Said4 id={label} as="span" />
        <span className="dollar-input">
          <span aria-hidden="true">$</span>
          <input
            type="text"
            inputMode="decimal"
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
            }}
          />
        </span>
      </label>
      {fractional && ask !== null && ask.amount > 0 && requestedAssessed !== null && (
        <p className="convert-line" data-held-copy="value.manual.convert">
          {say4With("value.manual.convert", {
            assessed: money({ basis: "assessed", amount: requestedAssessed.amount }),
            market: money(ask),
            pct: percentString(value.body.value.ratio.value),
            term: ratioTerm(state),
          })}
        </p>
      )}
    </Card4>
  );
}

// ── "Adjust for condition (optional)" (ValueView.swift:813-956) ────────────────────────────────────────────────────

function ConditionCard({
  value, state, busy, onRun, onUse,
}: {
  value: ValueResponse;
  state: string | null;
  busy: boolean;
  onRun: () => void;
  onUse: (n: number) => void;
}) {
  const c = value.body.condition;
  // `canUsePaidFeatures && hasConditionEvidence && anchorValue != nil` (:244-246), as the server states them.
  if (c.reason === "noConditionEvidence" || c.reason === "noAnchor") return null;
  const ratio = value.body.value.ratio;
  const ask = value.body.ask.requestedValue;
  const applied = c.adjustedValue !== null && ask !== null && ask.amount === c.adjustedValue.amount;
  const failed = c.reason === "modelFailed" || c.reason === "rateLimited" || c.reason === "modelUnavailable";
  return (
    <Card4 icon="wand" titleId="condition.title" tint="primary" footer={<Said4 id="condition.footer" />} data-condition={c.reason ?? "valued"}>
      {busy && c.deduction === null && (
        <ProgressLines4 ids={["condition.progress.1", "condition.progress.2", "condition.progress.3"]} />
      )}
      {c.deduction !== null && c.items.length === 0 && <Said4 id="condition.none" as="p" className="muted small" />}
      {c.deduction !== null && c.items.length > 0 && (
        <div className="condition-result">
          {c.items.map((item) => (
            <div key={item.label} className="condition-item">
              <div>
                <b>{item.label}</b>
                <span className="tags">
                  <span className="tag primary">{methodLabel(item.method)}</span>
                  {item.category !== "" && <span className="tag muted">{capitalized(item.category)}</span>}
                  {item.confidence === "low" && <Said4 id="condition.lowConfidence" as="span" className="tag accent" />}
                </span>
                {item.basis !== "" && <p className="faint">{item.basis}</p>}
              </div>
              <b className="reduction" data-held-copy="condition.reduction">{say4With("condition.reduction", { amount: money(item.amount) })}</b>
            </div>
          ))}
          {c.adjustedValue !== null && (
            <>
              <p className="condition-total">
                <Said4 id={ratio.fractional ? "condition.adjustedMarket" : "condition.adjusted"} />
                <b>{money(c.adjustedValue)}</b>
              </p>
              {c.capApplied && <Said4 id="condition.capped" as="p" className="faint" />}
              <button
                type="button"
                className="btn ghost block"
                disabled={applied || busy}
                onClick={() => onUse(c.adjustedValue!.amount)}
                data-held-copy={applied ? "condition.applied" : "condition.use"}
              >
                {applied ? say4("condition.applied") : say4With("condition.use", { amount: money(c.adjustedValue) })}
              </button>
              {ratio.fractional && c.adjustedAssessed !== null && (
                <p className="convert-line" data-held-copy="condition.marketNote">
                  {say4With("condition.marketNote", {
                    pct: percentString(ratio.value), term: ratioTerm(state), assessed: money(c.adjustedAssessed),
                  })}
                </p>
              )}
            </>
          )}
        </div>
      )}
      {c.deduction === null && !busy && (
        <>
          <button type="button" className="btn primary block" onClick={onRun} data-held-copy="condition.run">
            <Icon name="wand" size={16} /> {say4("condition.run")}
          </button>
          {failed && <Said4 id="condition.error" as="p" className="check-error" />}
        </>
      )}
    </Card4>
  );
}

// ── "Check uniformity (optional)" (ValueView.swift:1242-1516) ──────────────────────────────────────────────────────

function UniformityCard({
  check, value, state, busy, onAdopt, onWithdraw,
}: {
  check: EvaluateCaseResponse;
  value: ValueResponse;
  state: string | null;
  busy: boolean;
  onAdopt: (request: number | null) => void;
  onWithdraw: () => void;
}) {
  const u = check.body.uniformity!;
  // The app runs the check on the button; the server has already run it, so the button shows what it found.
  const [shown, setShown] = useState(u.adopted);
  const ratio = value.body.value.ratio;
  return (
    <Card4
      icon="scale"
      titleId="uniformity.title"
      tint="primary"
      footer={<Said4 id="uniformity.footer" values={{ state: uniformityState(state) }} />}
      data-uniformity={u.supportsRelief ? "relief" : "none"}
    >
      {!shown ? (
        <button type="button" className="btn primary block" onClick={() => setShown(true)} data-held-copy="uniformity.run">
          <Icon name="scale" size={16} /> {say4("uniformity.run")}
        </button>
      ) : (
        <div className="uniformity-result">
          {u.backfireRisk ? (
            <p className="backfire"><Icon name="warn" size={16} /> {u.headline}</p>
          ) : (
            <p className={u.supportsRelief ? "uniformity-head relief" : "uniformity-head"}>
              <Icon name={u.supportsRelief ? "seal" : "circle"} size={16} /> {u.headline}
            </p>
          )}
          {u.note !== null && <p className="muted small">{u.note}</p>}
          {u.supportsRelief && u.equityValue !== null && (
            <>
              <Said4 id={u.regime === "ratio_corridor" ? "uniformity.meaning.corridor" : "uniformity.meaning.parcels"} as="p" className="muted small" />
              {u.contestedBy !== null && (
                <p className="caution small" data-held-copy="uniformity.contested">
                  {say4With("uniformity.contested", { estimate: estimatePhrase(u.contestedBy) })}
                </p>
              )}
              {u.subjectRatio !== null && u.corridorLow !== null && u.corridorHigh !== null && (
                <Said4
                  id="uniformity.ratio"
                  as="p"
                  className="faint"
                  values={{ ratio: ratioPct(u.subjectRatio), low: ratioPct(u.corridorLow), high: ratioPct(u.corridorHigh) }}
                />
              )}
              {u.ratioImplausible && <Said4 id="uniformity.implausible" as="p" className="caution small" />}
              <button
                type="button"
                className={u.adopted ? "btn adopt adopted block" : "btn adopt block"}
                disabled={u.adopted || busy}
                onClick={() => onAdopt(u.adoptedRequest === null ? null : u.adoptedRequest.amount)}
                data-held-copy={u.adopted ? "uniformity.added" : u.lowersAsk ? "uniformity.useLower" : "uniformity.add"}
              >
                <Icon name={u.adopted ? "seal" : "scale"} size={16} />
                {u.adopted ? say4("uniformity.added")
                  : u.lowersAsk ? say4With("uniformity.useLower", { equity: money(u.equityValue) })
                    : say4("uniformity.add")}
              </button>
              {u.adopted && (
                <button type="button" className="link-button centered" disabled={busy} onClick={onWithdraw} data-held-copy="uniformity.remove">
                  <Icon name="back" size={14} /> {say4("uniformity.remove")}
                </button>
              )}
              {ratio.fractional && u.equityMarket !== null && (
                <Said4
                  id="uniformity.assessedNote"
                  as="p"
                  className="faint"
                  values={{ market: money(u.equityMarket), pct: percentString(ratio.value) }}
                />
              )}
              <p className="faint">
                {u.regime === "ratio_corridor" ? <Said4 id="uniformity.basis.corridor" />
                  : u.compCount > 0 ? <Said4 id="uniformity.basis.parcels" values={{ count: u.compCount }} />
                    : <Said4 id="uniformity.basis.none" />}
              </p>
            </>
          )}
        </div>
      )}
    </Card4>
  );
}

// ── the case-strength card (ValueView.swift:1577-1631) ─────────────────────────────────────────────────────────────

function StrengthCard({ check, value }: { check: EvaluateCaseResponse; value: ValueResponse }) {
  const meter = check.decisions["NEW-12"];
  if (!meter.ran) return null;
  const quality = check.body.caseQuality;
  const strongest = value.body.value.strongestComps;
  return (
    <section className="strength-card" data-strength={meter.value.filled}>
      <div className="strength-head">
        <Said4 id="strength.title" as="p" className="eyebrow muted-eyebrow" />
        <b data-copy-code={meter.value.label.code}>{meter.value.label.text}</b>
      </div>
      <MeterBars filled={meter.value.filled} />
      <p className="faint" data-copy-code={meter.value.detail.code}>{meter.value.detail.text}</p>
      {strongest !== null && (
        <p className="strongest-line"><Icon name="star" size={12} className="star" /> {strongestLine(strongest)}</p>
      )}
      {quality !== null && (
        <>
          <p className="quality-pair">
            <b>{meterHeadline(meter.value.label.text)}</b>
            <span aria-hidden="true">·</span>
            <span>{precisionLabel(quality.precision)}</span>
          </p>
          <p className="faint" data-copy-code={quality.blurb.code}>{quality.blurb.text}</p>
          {quality.caveat !== null && <p className="quality-caveat" data-copy-code={quality.caveat.code}>{quality.caveat.text}</p>}
        </>
      )}
    </section>
  );
}

// ── "Grounds for your appeal" (ValueView.swift:1651-1725, the ReasonTile :1856-1904) ──────────────────────────────

function ReasonTile({
  reason, selected, disabled, onToggle, children,
}: {
  reason: SelectableReason;
  selected: boolean;
  disabled: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="reason">
      <button
        type="button"
        className={selected ? "reason-tile on" : "reason-tile"}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onToggle}
        data-reason={reason}
      >
        <span className="reason-icon" aria-hidden="true"><Icon name={REASON_ICON[reason]} size={16} /></span>
        <span className="reason-text">
          <Said4 id={`reason.${reason}` as Parity4WordingId} as="b" />
          <Said4 id={`reason.${reason}.blurb` as Parity4WordingId} as="small" />
        </span>
        <Icon name={selected ? "checkCircle" : "circle"} size={22} className="reason-check" />
      </button>
      {children}
    </div>
  );
}

/** "What's incorrect in the county record?" (ValueView.swift:1705-1723), saved when the owner leaves the field. */
function RecordNote({ saved, onSave }: { saved: string; onSave: (text: string) => void }) {
  const [text, setText] = useState(saved);
  const id = "record-error-note";
  return (
    <div className="record-note" data-record-note>
      <label htmlFor={id} data-held-copy="grounds.record.label"><b>{say4("grounds.record.label")}</b></label>
      <textarea
        id={id}
        value={text}
        rows={3}
        placeholder={say4("grounds.record.placeholder")}
        className={text.trim() === "" ? "needs" : undefined}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text !== saved) onSave(text);
        }}
      />
      <Said4
        id={text.trim() === "" ? "grounds.record.empty" : "grounds.record.filled"}
        as="p"
        className={text.trim() === "" ? "caption warn" : "caption"}
      />
    </div>
  );
}
